import AvatarDefaultIcon from '@assets/images/avatar/default.png';
import Logo from '@assets/images/common/brand/logo-primary.svg';
import ConstructionIcon from '@assets/images/common/icon/construction/construction-gary.svg';
import LockIcon from '@assets/images/common/icon/lock/lock.svg';
import LockIconPrimary from '@assets/images/common/icon/lock/lock-primary.svg';
import { useCompanyAuth } from '@src/context/auth/useCompanyAuth';
import { useUserNavigation } from '@src/context/UserNavigationContext';
import { useClickOutside } from '@src/hooks/useClickOutside';
import { useCompanyInAppNotificationPolling } from '@src/hooks/useCompanyInAppNotificationPolling';
import { COMPANY_URL } from '@src/routes/company';
import Button from '@src/ui/elements/button/Button';
import UserGuideLink from '@src/ui/elements/button/UserGuideLink';
import { BlackBell } from '@src/ui/elements/icon/bell/BlackBell';
import { CompanyInAppNotificationTooltip } from '@src/ui/elements/tooltip/InAppNotificationTooltip';
import { NavItem } from '@src/ui/templates/Company/NavMenu/types';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const renderLockIcon = (current: boolean) => (
  <img
    src={current ? LockIconPrimary : LockIcon}
    alt=''
    width='24'
    height='24'
  />
);

interface MobileHeaderProps {
  navItems: NavItem[];
}

const MobileHeader = ({ navItems }: MobileHeaderProps) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [inAppNotificationTooltipOpen, setInAppNotificationTooltipOpen] = useState(false);
  const [isFeatureListOpen, setIsFeatureListOpen] = useState(false);
  const [isManagementListOpen, setIsManagementListOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const { menuLinks } = useUserNavigation();
  const companyAuth = useCompanyAuth();

  const isEnabledInAppNotificationTooltip =
    companyAuth.profile &&
    companyAuth.profile.isLogin &&
    companyAuth.profile.tenant?.service.online_community;

  const { notifications, isExistNewNotification, resetNewNotification } =
    useCompanyInAppNotificationPolling(isEnabledInAppNotificationTooltip ?? false);

  const handleMenu = (setter: React.Dispatch<React.SetStateAction<boolean>>) => {
    setIsOpen(!isOpen);
    setIsFeatureListOpen(false);
    setIsManagementListOpen(false);
    toggle(setter);
  };

  const toggle = (setter: React.Dispatch<React.SetStateAction<boolean>>) => {
    setter((prevState) => !prevState);
  };

  const inAppNotificationToggle = (e?: React.MouseEvent) => {
    e?.preventDefault();
    setInAppNotificationTooltipOpen((prev) => !prev);
    resetNewNotification();
  };

  useClickOutside([tooltipRef], () => setInAppNotificationTooltipOpen(false));

  const [vw, setVw] = useState<number>();

  useLayoutEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const update = () => {
      const w = Math.round(
        window.visualViewport?.width ?? window.screen?.width ?? window.innerWidth,
      );
      setVw(w);
    };
    update();

    // visualViewport は resize / scroll の両方で幅が変わる
    const vv = window.visualViewport || null;
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    window.addEventListener('orientationchange', update);
    window.addEventListener('resize', update);

    return () => {
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
      window.removeEventListener('orientationchange', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <>
      <header
        className='fixed left-0 top-0 z-[9999] hidden h-[60px] items-center justify-between border-b border-solid border-b-gray-200 bg-white lg:flex'
        style={{ width: vw ? `${vw}px` : undefined }}
      >
        {!isOpen && (
          <button
            className='relative z-50 ml-[10px] flex h-[60px] w-[45px] items-center justify-center'
            onClick={() => handleMenu(setIsFeatureListOpen)}
            aria-expanded={isOpen}
            aria-label='メニュー'
          >
            <span
              className={`absolute inset-0 m-auto h-[2px] w-[18px] translate-y-[-5px] rounded-md bg-primary transition-all`}
            />
            <span
              className={`absolute inset-0 m-auto h-[2px] w-[18px] rounded-md bg-primary transition-all `}
            />
            <span
              className={`absolute inset-0 m-auto h-[2px] w-[18px] translate-y-[5px] rounded-md bg-primary transition-all`}
            />
          </button>
        )}
        <Link
          to={COMPANY_URL.HOME}
          className={`block ${isEnabledInAppNotificationTooltip ? 'mr-[-20px]' : ''} ${isOpen && 'px-[24px]'}`}
        >
          <img
            src={Logo}
            alt=''
            className='block h-auto max-h-[40px] w-[220px] object-contain'
          />
        </Link>
        <div className='flex shrink-0 items-center'>
          {isEnabledInAppNotificationTooltip && (
            <BlackBell
              handleToggle={inAppNotificationToggle}
              notifications={notifications}
              isExistNewNotification={isExistNewNotification}
            />
          )}
          <button
            className={`relative z-50 ${isEnabledInAppNotificationTooltip ? 'mr-[20px] size-[20px]' : 'size-[60px]'} flex items-center justify-center`}
            onClick={() => handleMenu(setIsManagementListOpen)}
            aria-expanded={isOpen}
            aria-label='メニュー'
          >
            <span
              className={`absolute inset-0 m-auto size-[4px] translate-y-[-6px] rounded-md bg-primary transition-all ${
                isOpen ? 'h-[2px] w-[18px] !translate-y-0 rotate-45' : ''
              }`}
            />
            <span
              className={`absolute inset-0 m-auto size-[4px] rounded-md bg-primary transition-all ${
                isOpen ? 'h-[2px] w-[18px] !translate-x-1 opacity-0' : ''
              }`}
            />
            <span
              className={`absolute inset-0 m-auto size-[4px] translate-y-[6px] rounded-md bg-primary transition-all ${
                isOpen ? 'h-[2px] w-[18px] !translate-y-0 -rotate-45' : ''
              }`}
            />
          </button>
        </div>
      </header>
      {/* ドロワーメニュー */}
      <div
        className={`fixed inset-x-0 top-[60px] z-[9998] h-[calc(100%-60px)] bg-white transition-all duration-200 ${
          isOpen ? 'visible translate-y-0 opacity-100' : 'invisible -translate-y-2 opacity-0'
        }`}
      >
        <nav className='flex h-full flex-col px-6 py-5'>
          {isFeatureListOpen && (
            <ul className='flex flex-col'>
              {navItems.map((item: NavItem, index: number) => (
                <li
                  key={index}
                  className={`relative
                  ${item.current ? 'border-b-2 border-primary font-semibold text-primary' : 'border-white'}
                  ${item.isComingSoon ? 'text-gray-400' : ''}
                  ${index < 4 ? 'flex-[0_0_auto]' : ''}
                `}
                >
                  {item.isComingSoon ? (
                    <>
                      <span className='flex items-center px-2 py-[18px]'>
                        <img
                          src={ConstructionIcon}
                          alt=''
                          width='24'
                          height='24'
                        />
                        <span>{item.title}</span>
                      </span>
                    </>
                  ) : (
                    <button
                      className='flex items-center px-2 py-[18px]'
                      onClick={() => navigate(item.link)}
                    >
                      {companyAuth.profile?.isLogin &&
                      !companyAuth.profile.user?.isAdmin &&
                      item.adminOnly ? (
                        <span
                          className={`mr-[4px] translate-y-px border border-solid border-current px-1 text-[10px] ${item.current ? 'text-primary' : 'text-black'}`}
                        >
                          管理者
                        </span>
                      ) : (
                        ''
                      )}
                      {item.isLocked ? renderLockIcon(item.current) : ''}
                      <span>{item.title}</span>
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {isManagementListOpen && (
            <>
              {companyAuth.profile?.isLogin ? (
                <div className='mt-5 flex flex-col gap-[20px]'>
                  <div className='flex items-center gap-4 px-3'>
                    <img
                      className='size-6 rounded-full object-cover'
                      src={companyAuth.profile.user?.avatarUrl || AvatarDefaultIcon}
                      alt=''
                      width='24'
                      height='24'
                    />
                    <p className='grow truncate'>
                      <span className='block truncate text-sm font-semibold'>
                        {`${companyAuth.profile.user?.familyName || ''} ${companyAuth.profile.user?.firstName || ''}`}
                      </span>
                    </p>
                  </div>
                  <div className='flex flex-col gap-px'>
                    {menuLinks.map((link) => (
                      <>
                        {link.link === COMPANY_URL.LOGOUT ? (
                          <div
                            className='mt-7 flex flex-col gap-[20px]'
                            style={{ width: vw ? `${Math.max(vw - 50, 200)}px` : undefined }}
                          >
                            <Button
                              variant='quaternary'
                              outline
                              onClick={() => navigate(link.link)}
                            >
                              ログアウト
                            </Button>
                          </div>
                        ) : (
                          <Link
                            to={link.link}
                            className='cursor-pointer px-3 py-[20px] transition-colors'
                          >
                            {link.title}
                          </Link>
                        )}
                      </>
                    ))}
                  </div>
                </div>
              ) : (
                <div className='mt-7 flex flex-col gap-[10px] px-12'>
                  <Button
                    size='s'
                    fullRounded
                    outline
                    onClick={() => navigate(COMPANY_URL.LOGIN)}
                  >
                    ログイン
                  </Button>

                  <Button
                    size='s'
                    fullRounded
                    outline
                    onClick={() => navigate(COMPANY_URL.REGISTRATION)}
                  >
                    新規会員登録
                  </Button>
                </div>
              )}
              <UserGuideLink
                userType='company'
                device='mobile'
                color='gray'
              />
            </>
          )}{' '}
        </nav>
      </div>
      {isEnabledInAppNotificationTooltip && inAppNotificationTooltipOpen && (
        <div className='absolute right-5 top-[52px] z-[99999] lg:left-1/2 lg:right-auto lg:top-[65px] lg:-translate-x-1/2'>
          <CompanyInAppNotificationTooltip
            notifications={notifications}
            onCancel={inAppNotificationToggle}
          />
        </div>
      )}
    </>
  );
};

export default MobileHeader;
